/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-refresh/only-export-components */
import Cookies from "js-cookie";
import { UserRole } from "@/types";
import { systemRoles } from "@/atoms";
import { useRecoilState } from "recoil";
import { AuthApi } from "@/axios.config.ts";
import Logo from "@/components/common/Logo";
import { CouncilDTO, IUser, Role, TblCouncil, UserDTO } from "@/types/user.type";
import { notifications } from "@mantine/notifications";
import { getUseRoute } from "@/utils/userroutes/loginroutes2";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import React, { createContext, useContext, useEffect, useState, useRef } from "react";

interface IAuthContext {
  user: IUser | UserDTO | null;
  initialLoading: boolean;
  logout: () => Promise<void>;
  getRoles: () => Promise<void>;
  getProfile: () => Promise<void>;
  loading: boolean;
  profile: {
    user: IUser | UserDTO;
    employee: any;
    roles: Role[];
  } | null;
  isAdmin: boolean;
}

const AuthContext = createContext<IAuthContext | undefined>(undefined);

export const nonProtectedRoutes = [
  "/HomePage",
  // This list is matched case-sensitively, but the route is registered
  // lowercase, so both spellings must be public for the
  // registration-disabled notice to always be reachable.
  "/homepage",
  "/verify-certificate",
  "/auth/login",
  "/auth/forgot-password",
  "/auth/verify-email",
  "/auth/reset-password",
  "/auth/forgot-password-bylicense",
  "/auth/accept-invitation",
  "/create-account/CreateAccount",
  "/certificate-details/ViewCertificateDetails",
  "/auth/reset-password",
];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<IUser | UserDTO | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [roles, setRoles] = useRecoilState(systemRoles);
  const [isAdmin, setIsAdmin] = useState(false);
  const token = Cookies.get("token");
  const isWhiteListed = nonProtectedRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  // Refs to prevent duplicate calls
  const profileFetchedRef = useRef(false);
  const rolesFetchedRef = useRef(false);
  const profileFetchingRef = useRef(false);
  const rolesFetchingRef = useRef(false);

  const getProfile = async () => {
    if (profileFetchingRef.current) return;

    profileFetchingRef.current = true;
    setLoading(true);
    try {
      const res = await AuthApi.get("/auth/profile");
      const data = res.data.data;

      const normalizedRoles = (data.roles ?? []).map(
        (r: { id?: string; roleId?: string; roleName?: string; name?: string; code?: string }) => ({
          roleId: r.roleId ?? r.id ?? "",
          roleName: (r.roleName ?? r.name ?? "") as UserRole,
          code: r.code,
        })
      );

      const councilSource = data.usercouncil?.length
        ? (data.usercouncil as CouncilDTO[])
        : data.councils?.length
          ? (data.councils as CouncilDTO[])
          : [];

      const usercouncil = councilSource.length
        ? councilSource.map((c: CouncilDTO & { registrarName?: string | null }) => ({
            id: c.id,
            councilCode: c.councilCode,
            name: c.name,
            councilName: c.name,
            councilProfileDocumentPath: c.councilProfileDocumentPath ?? "",
            amount: 0,
            status: "",
            registrarName: c.registrarName ?? null,
          })) as TblCouncil[]
        : undefined;

      const userData = {
        ...data,
        roles: normalizedRoles,
        usercouncil,
        street_name_and_number: data.street_name_and_number ?? data.streetNameAndNumber ?? "",
        postal_address: data.postal_address ?? data.postalAddress ?? "",
        house_or_plot_number: data.house_or_plot_number ?? data.houseOrPlotNumber ?? "",
      };
      setUser(userData);
      setProfile({
        user: data,
        roles: normalizedRoles,
        employee: data?.employee,
      });
      setIsAdmin(normalizedRoles.some((role: Role) => role.roleName === UserRole.ADMIN));
      profileFetchedRef.current = true;
    } catch (error) {
      console.log(error);
      setInitialLoading(false);
    } finally {
      setLoading(false);
      profileFetchingRef.current = false;
    }
  };

  const getRoles = async () => {
    // Prevent concurrent calls
    if (rolesFetchingRef.current) return;
    
    // Only fetch roles if we have a token (authenticated)
    if (!token) {
      return;
    }
    
    rolesFetchingRef.current = true;
    try {
      const res = await AuthApi.get("/roles/all");
      setRoles(res.data.data as Role[]);
      rolesFetchedRef.current = true;
    } catch (error) {
      // Silently handle 401 errors (user not authenticated)
      // This is expected on public pages
      if ((error as any)?.response?.status !== 401) {
        console.log(error);
      }
    } finally {
      rolesFetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Only fetch if not already fetched and we have a token
    if (token && (!roles || !roles.length) && !rolesFetchedRef.current) {
      getRoles();
    }
    if (token && !profileFetchedRef.current) {
      getProfile();
    }
  }, [token]);

  useEffect(() => {
    if (token && roles.length && profile && user) setInitialLoading(false);
    else if (!token) setInitialLoading(false);
  }, [roles, user, profile]);

  const logout = async () => {
    setLoading(true);
    setUser(null);
    setProfile(null);
    Cookies.remove("token");
    // Reset refs to allow fetching again on next login
    profileFetchedRef.current = false;
    rolesFetchedRef.current = false;
    setLoading(false);
    notifications.show({
      title: "Success",
      message: "Logged out successfully",
      color: "green",
    });
    navigate("/auth/login");
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-screen ">
        <div className="animate-pulse flex flex-col  items-center">
          <Logo />
          <div className="flex items-center mt-4 gap-4">
            <LoadingSpinner />
            <h1 className="opacity-60"> Loading...</h1>
          </div>
        </div>
      </div>
    );
  }
  if (isWhiteListed && user && token) {
    const nextUrl = getUseRoute(profile?.roles?.[0]?.roleName);
    return <Navigate to={nextUrl} />;
  }

  if (!user && !initialLoading && !isWhiteListed) {
    return <Navigate to="/auth/login" />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        getRoles,
        getProfile,
        initialLoading,
        logout,
        loading,
        profile,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}